/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {useEffect, useState} from 'react';
import {Button, Divider, Form, Header, Icon} from 'semantic-ui-react';
import {useTranslation} from 'react-i18next';
import {getItemTypes} from '../../../services/ItemType/ItemType.service';
import UltyIndeterminateCheckboxGroup from '../../Shared/UltyIndeterminateCheckboxGroup/UltyIndeterminateCheckboxGroup';
import useUser from '../../Shared/UserProvider/useUser';
import {UltyInputText} from '@ulty/ulty-ui';
import AdvancedMenuFilters from './AdvancedMenuFilters';
import { isProviderAPos } from '../../../services/Provider/Provider.service';
import { ultyToast } from '../../Shared/UltyToast/UltyToast'

const categoryStyles = css`
  display: flex;
  justify-content: space-evenly;
  margin-bottom: 1rem;

  @media only screen and (max-width: 600px) {
    flex-direction: column;

    &>div {
        padding: 1em;
    }
  }
`

const AdvancedFilters = ({defaultFilters, onFiltersChange}) => {
    const {user} = useUser();
    const [t] = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [itemTypesFilterOptions, setItemTypesFilterOptions] = useState([]);
    const [measurementTypeFilterOptions, setMeasurementTypeFilterOptions] = useState(
        ['COUNT', 'WEIGHT', 'VOLUME'].map(type => ({
            label: t(`global.enums.measurementTypes.${type}`),
            checked: defaultFilters.measurementUnitType !== undefined ? defaultFilters.measurementUnitType.includes(type) : true,
            value: type,
        }))
    );
    const [providerTypeFilterOptions, setProviderTypeFilterOptions] = useState([
        {label: t('provider.type_COMPANY'), checked: defaultFilters.providerType !== undefined ? defaultFilters.providerType === 'COMPANY' : true, value: 'COMPANY'},
        {label: t('provider.type_POINT_OF_SALE'), checked: defaultFilters.providerType !== undefined ? defaultFilters.providerType === 'POINT_OF_SALE' : true, value: 'POINT_OF_SALE'},
    ]);
    const [containsAlcoholFilterOptions, setContainsAlcoholFilterOptions] = useState([
        {label: t('product_list.advanced_filters.general.alcohol.with'), checked: defaultFilters.containsAlcohol !== undefined ? defaultFilters.containsAlcohol : true, value: true},
        {label: t('product_list.advanced_filters.general.alcohol.without'), checked: defaultFilters.containsAlcohol !== undefined ? !defaultFilters.containsAlcohol : true, value: false}
    ]);
    const [imageFilterOptions, setImageFilterOptions] = useState([
        {label: t('product_list.advanced_filters.general.image.with'), checked: defaultFilters.withImage !== undefined ? defaultFilters.withImage : true, value: true},
        {label: t('product_list.advanced_filters.general.image.without'), checked: defaultFilters.withImage !== undefined ? !defaultFilters.withImage : true, value: false}
    ]);
    const [unavailableFilterOptions, setUnavailableFilterOptions] = useState([
        {label: t('product_list.advanced_filters.inventory.availability.available'), checked: defaultFilters.unavailableUntil !== undefined ? !defaultFilters.unavailableUntil : true, value: false},
        {label: t('product_list.advanced_filters.inventory.availability.unavailable'), checked: defaultFilters.unavailableUntil !== undefined ? defaultFilters.unavailableUntil : true, value: true}
    ]);

    const [filters, setFilters] = useState(defaultFilters);

    useEffect(() => {
        (async function loadItemTypes() {
            try {
                setIsLoading(true);
                const types = await getItemTypes(['PRODUCT', 'MENU', 'EXTRA']);
                setItemTypesFilterOptions(types.map(type => ({
                    label: t(`global.enums.itemTypes.${type.name}`),
                    checked: defaultFilters.types !== undefined ? defaultFilters.types.includes(type.name) : true,
                    value: type.name
                })));
            } catch (e) {
                console.log(e);
                ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
            } finally {
                setIsLoading(false);
            }
        })();
    }, [defaultFilters.types, t]);

    const handleIndeterminateInclusiveValuesCheckboxGroupChange = (selectedValues, filterOptionsValues) => {
        const newFilters = { ...filters };
        const noneSelected = selectedValues.length === 0;

        if (noneSelected) {
            delete newFilters.types;
        } else {
            newFilters.types = selectedValues;
        }

        setFilters(newFilters);
    }

    const handleMeasurementTypeChange = (selectedValues) => {
        const newFilters = { ...filters };
        if (selectedValues.length === 0 || selectedValues.length === 3) {
            delete newFilters.measurementUnitType;
        } else {
            newFilters.measurementUnitType = selectedValues;
        }
        setFilters(newFilters);
    }

    const handleProviderTypeChange = (selectedValues) => {
        const newFilters = { ...filters };
        if (selectedValues.length === 0 || selectedValues.length === 2) {
            delete newFilters.providerType;
        } else {
            newFilters.providerType = selectedValues[0];
        }
        setFilters(newFilters);
    }

    const handleIndeterminateExclusiveValuesCheckboxGroupChange = (filterName, selectedValues) => {
        const newFilters = { ...filters };
        const trueValueSelected = selectedValues.includes(true);
        const falseValueSelected = selectedValues.includes(false);

        const allSelected = trueValueSelected && falseValueSelected;
        const noneSelected = !trueValueSelected && !falseValueSelected;

        if (allSelected || noneSelected) {
            delete newFilters[filterName];
        } else {
            newFilters[filterName] = !!selectedValues[0];
        }

        setFilters(newFilters);
    }

    const defineMaxQuantityFilter = (maxQuantityValue) => {
        const newFilters = { ...filters };

        if (maxQuantityValue === '') {
            const { maxQuantity, ...filtersWithoutMaxQuantity } = newFilters;
            setFilters(filtersWithoutMaxQuantity);
        } else {
            newFilters.maxQuantity = maxQuantityValue;
            setFilters(newFilters);
        }
    }

    const handleMaxQuantityChange = (e) => {
        defineMaxQuantityFilter(e.target.value);
    }

    const handleSetMaxQuantityToPopMinimumThreshold = () => {
        defineMaxQuantityFilter(user.provider.object.stockUnavailableThreshold);
    }

    const handleFilters = () => {
        onFiltersChange(filters);
    };

    return(
        <Form css={css`
          display: flex;
          flex-direction: column;
        `}>
            <Divider horizontal>
                <Header as='h4'>
                    <Icon name='globe' color='teal' />
                    {t('product_list.advanced_filters.general.general')}
                </Header>
            </Divider>

            <div
                css={categoryStyles}>
                <UltyIndeterminateCheckboxGroup
                    parentLabel={t('product_list.advanced_filters.general.item_types')}
                    loading={isLoading}
                    options={itemTypesFilterOptions}
                    onChange={({checkedValues}) => {
                        setItemTypesFilterOptions(prev => (
                            prev.map(p => {
                                p.checked = checkedValues.includes(p.value)
                                return p
                            })
                        ));
                        handleIndeterminateInclusiveValuesCheckboxGroupChange(checkedValues, itemTypesFilterOptions.map(filterOption => filterOption.value));
                    }}
                />

                <UltyIndeterminateCheckboxGroup
                    parentLabel={t('product_list.advanced_filters.general.alcohol.alcohol')}
                    options={containsAlcoholFilterOptions}
                    onChange={({checkedValues}) => {
                        setContainsAlcoholFilterOptions(prev => (
                            prev.map(p => {
                                p.checked = checkedValues.includes(p.value)
                                return p
                            })
                        ));
                        handleIndeterminateExclusiveValuesCheckboxGroupChange('containsAlcohol', checkedValues);
                    }}
                />

                <UltyIndeterminateCheckboxGroup
                    parentLabel={t('product_list.advanced_filters.general.image.image')}
                    options={imageFilterOptions}
                    onChange={({checkedValues}) => {
                        setImageFilterOptions(prev => (
                            prev.map(p => {
                                p.checked = checkedValues.includes(p.value)
                                return p
                            })
                        ));
                        handleIndeterminateExclusiveValuesCheckboxGroupChange('withImage', checkedValues);
                    }}
                />

                <UltyIndeterminateCheckboxGroup
                    parentLabel={t('product_list.advanced_filters.general.measurement_type')}
                    options={measurementTypeFilterOptions}
                    onChange={({checkedValues}) => {
                        setMeasurementTypeFilterOptions(prev => prev.map(p => ({...p, checked: checkedValues.includes(p.value)})));
                        handleMeasurementTypeChange(checkedValues);
                    }}
                />

                <AdvancedMenuFilters filters={filters} setFilters={setFilters} />
            </div>

            {isProviderAPos(user.provider) &&
            <>
                <Divider horizontal>
                    <Header as='h4'>
                        <Icon name='warehouse' color='teal' />
                        {t('product_list.advanced_filters.inventory.pos')}
                    </Header>
                </Divider>

                <div css={categoryStyles}>
{/* 
                    <UltyIndeterminateCheckboxGroup
                       parentLabel={t('product_list.advanced_filters.general.provider_type')}
                       options={providerTypeFilterOptions}
                       onChange={({checkedValues}) => {
                           setProviderTypeFilterOptions(prev => prev.map(p => ({...p, checked: checkedValues.includes(p.value)})));
                           handleProviderTypeChange(checkedValues);
                       }}
                   /> */}

                    <UltyIndeterminateCheckboxGroup
                        parentLabel={t('product_list.advanced_filters.inventory.availability.availability')}
                        options={unavailableFilterOptions}
                        onChange={({checkedValues}) => {
                            setUnavailableFilterOptions(prev => (
                                prev.map(p => {
                                    p.checked = checkedValues.includes(p.value)
                                    return p
                                })
                            ));
                            handleIndeterminateExclusiveValuesCheckboxGroupChange('unavailableUntil', checkedValues);
                        }}
                    />


                    <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                          align-items: flex-start;
                        `}
                    >
                        <UltyInputText
                            label={t('product_list.advanced_filters.inventory.quantity.available_quantity')}
                            type='number'
                            min={-1}
                            value={filters.maxQuantity ?? ''}
                            onChange={handleMaxQuantityChange}
                        />
                        <Button
                            type='button'
                            color='teal'
                            basic
                            onClick={handleSetMaxQuantityToPopMinimumThreshold}
                        >
                            {`${t('product_list.advanced_filters.inventory.quantity.pos_minimum_threshold')}: ${user.provider.object.stockUnavailableThreshold}`}
                        </Button>
                    </div>

                </div>
            </>
            }

            <div css={css`
              display: flex;
              margin-top: 1rem;
              justify-content: flex-end;
            `}>
                <Button
                    color='teal'
                    onClick={handleFilters}
                >
                    {t('global.validate')}
                </Button>
            </div>
        </Form>
    )
}

export default AdvancedFilters;
